import type { Timestamp } from "@firebase/firestore-types";
import { z } from "zod";
import { accountFullPath } from "../account/models.js";
import type { AuthRole } from "../auth.js";

export const commentsPathFromAccount = "comments";

export const commentsFullPath = (accountId: string): string =>
  `${accountFullPath(accountId)}/${commentsPathFromAccount}`;

export const commentTargetTypeSchema = z.enum(["transaction", "transferRequest"]);
export type CommentTargetType = z.infer<typeof commentTargetTypeSchema>;

export const commentTargetSchema = z.object({
  targetType: commentTargetTypeSchema,
  targetId: z.string().min(1),
});
export type CommentTarget = z.infer<typeof commentTargetSchema>;

export const commentTargetKey = ({ targetType, targetId }: CommentTarget) =>
  `${targetType}-${targetId}`;

export const commentTextSchema = z.string().trim().min(1).max(5000);

export const optionalCommentTextSchema = z
  .string()
  .trim()
  .max(5000)
  .optional()
  .transform((text) => text || undefined);

export const transferRequestCommentId = (transferRequestId: string) =>
  `transferRequest-${transferRequestId}`;

/**
 * Append-only discussion on a transaction or a transfer request.
 * Written by the `addComment` callable, the Swan banking API, or transfer request creation
 * (callable, Swan banking, public API). Never edited or deleted.
 */
export type Comment = {
  targetType: CommentTargetType;
  /** Transaction or transfer request document id in the account's provider collection. */
  targetId: string;
  text: string;
  author:
    | {
        source: "aircap";
        uid: string;
        email: string;
        role: AuthRole | "admin";
      }
    | {
        source: "swan";
        accountMembershipId: string;
        email: string;
        name: string | undefined;
      }
    | {
        source: "api";
        supervisorOrgId: string;
        name: string;
      };
  createdAt: Timestamp;
};

export const commentReadersPathFromAccount = "commentReaders";

export const commentReaderFullPath = (accountId: string, readerId: string): string =>
  `${accountFullPath(accountId)}/${commentReadersPathFromAccount}/${readerId}`;

/** An Aircap reader is keyed by uid. */
export const swanCommentReaderId = (accountMembershipId: string) => `swan-${accountMembershipId}`;

/**
 * Unread comments of one person on an account, written only by the server: the comment
 * trigger adds each comment for the people it notifies, and opening the thread clears it.
 */
export type CommentReader = {
  reader: { source: "aircap"; uid: string } | { source: "swan"; accountMembershipId: string };
  /** Unread comment ids, keyed by `commentTargetKey`. */
  unreadCommentIds: Record<string, string[]>;
  unreadCount: number;
};
